import { ReactNode } from 'react';

import { ColorInputSize, ColorType } from '../../../types/ColorPicker';
import { extractGradientColors } from '../../../utils/extractGradientColors';
import { isGradientColor } from '../../../utils/isGradientColor';
import { isVar } from '../../../utils/isVar';
import { useConvertCssValueToHexaColor } from '../../../utils/useConvertCssValueToHexaColor';
import { ColorInput } from '../../inputs/ColorInput';
import { ColorPickerPopover } from '../../pickers/ColorPickerPopover';

type ColorInputPopoverProps = {
  color: string;
  onSelectColor: ({ color, type }: { color: string; type: ColorType }) => void;
  size?: ColorInputSize;
  withGradient?: boolean;
  themeColors: string[];
  onDelete?: () => void;
  onReset?: () => void;
  editComponent?: ReactNode;
  dataTestId?: string;
};

export function ColorInputPopover(props: ColorInputPopoverProps) {
  const {
    color,
    onSelectColor,
    withGradient = false,
    size = ColorInputSize.large,
    themeColors,
    onDelete,
    onReset,
    editComponent,
    dataTestId
  } = props;

  const resolveColor = useConvertCssValueToHexaColor();

  const getResolvedColor = (color: string) => {
    if (isGradientColor(color)) {
      const colors = extractGradientColors(color);
      let resolvedColor = color;
      if (isVar(colors?.color1)) {
        const color1 = resolveColor(colors.color1);
        resolvedColor = resolvedColor.replace(colors.color1, color1);
      }
      if (isVar(colors?.color2)) {
        const color2 = resolveColor(colors.color2);
        resolvedColor = resolvedColor.replace(colors.color2, color2);
      }
      return resolvedColor;
    } else if (isVar(color)) {
      return resolveColor(color);
    } else {
      return color;
    }
  };

  const resolvedColor = getResolvedColor(color);

  return (
    <ColorPickerPopover
      color={resolvedColor}
      onSelectColor={onSelectColor}
      withGradient={withGradient}
      themeColors={themeColors}
      editComponent={editComponent}
      renderPopoverTarget={props => <ColorInput onDelete={onDelete} onReset={onReset} size={size} {...props} />}
      data-testid={dataTestId}
    />
  );
}
